import { useState } from 'react';
import type React from 'react';

import { BarChart } from 'components/partials/charts';
import { validateBarChartProps } from 'components/partials/charts/shared/chart-validation';
import type { ChartLabelFormat } from 'components/partials/charts/shared/types';
import ChartPartFrame from './chart-part-frame';
import type { ChartViewMode } from './chart-part-frame';
import ChartPartTable from './chart-part-table';
import InvalidChartNotice from './invalid-chart-notice';

type MarkChartType = 'BAR';
type MarkFormat = 'NUMBER' | 'PERCENT' | 'CURRENCY';

interface MarkBarChartData {
  chartType?: unknown;
  title?: unknown;
  subtitle?: unknown;
  format?: unknown;
  categoryAxis?: {
    label?: unknown;
    categories?: unknown;
  } | null;
  series?: ReadonlyArray<{
    label?: unknown;
    values?: unknown;
  } | null> | null;
}

export interface ChartPartProps {
  data: unknown;
}

const FORMAT_MAP: Record<MarkFormat, ChartLabelFormat> = {
  NUMBER: 'number',
  PERCENT: 'percent',
  CURRENCY: 'currency',
};

const INVALID_CHART_MESSAGE =
  'This chart could not be displayed. Try asking your question again.';

const getChartFormat = (format: unknown): ChartLabelFormat => {
  if (typeof format !== 'string') return 'number';

  return FORMAT_MAP[format as MarkFormat] ?? 'number';
};

const isBarChartType = (chartType: unknown): chartType is MarkChartType =>
  chartType === 'BAR';

const asBarChartPayload = (data: unknown): MarkBarChartData | null => {
  if (!data || typeof data !== 'object') return null;

  return data as MarkBarChartData;
};

const buildTableCaption = (title?: string, subtitle?: string): string => {
  const descriptor = [title, subtitle].filter(Boolean).join(' - ');

  return descriptor ? `Data table for ${descriptor}` : 'Data table for chart';
};

const ChartPart = ({ data }: ChartPartProps): React.JSX.Element => {
  const [viewMode, setViewMode] = useState<ChartViewMode>('chart');
  const payload = asBarChartPayload(data);

  if (!payload || !isBarChartType(payload.chartType)) {
    return <InvalidChartNotice message={INVALID_CHART_MESSAGE} />;
  }

  const validation = validateBarChartProps({
    categoryAxis: payload.categoryAxis,
    series: payload.series,
    format: getChartFormat(payload.format),
  });

  if (!validation.valid) {
    return <InvalidChartNotice message={INVALID_CHART_MESSAGE} />;
  }

  const { normalized } = validation;
  const title = typeof payload.title === 'string' ? payload.title : undefined;
  const subtitle =
    typeof payload.subtitle === 'string' ? payload.subtitle : undefined;
  const tableCaption = buildTableCaption(title, subtitle);

  return (
    <ChartPartFrame
      title={title}
      subtitle={subtitle}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
    >
      {viewMode === 'chart' ? (
        <BarChart
          data={{
            categoryAxis: normalized.categoryAxis,
            series: normalized.series,
            format: normalized.format,
          }}
          className="w-full"
        />
      ) : (
        <ChartPartTable data={normalized} caption={tableCaption} />
      )}
    </ChartPartFrame>
  );
};

export default ChartPart;
